
import { Suspense, lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { AdminAuthGuard } from '../features/auth/AdminAuthGuard';
import { PortalAdminShell } from '../components/layout/PortalAdminShell';

const LoginPage = lazy(() => import('../features/auth/LoginPage'));
const DashboardPage = lazy(() => import('../features/dashboard/PortalDashboardPage'));
const AccountsPage = lazy(() => import('../features/clients/PortalClientsPage'));
const CreateAccountPage = lazy(() => import('../features/clients/CreatePortalClientPage'));
const AccountDetailPage = lazy(() => import('../features/clients/PortalClientDetailPage'));
const WebProjectsPage = lazy(() => import('../features/web-projects/V4ProjectsPage'));
const V4ProjectCreatePage = lazy(() => import('../features/web-projects/V4ProjectCreatePage'));
const WebProjectDetailPage = lazy(() => import('../features/web-projects/V4ProjectDetailPage'));
const AuditPage = lazy(() => import('../features/audit/AuditPage'));
const InquiriesPage = lazy(() => import('../features/inquiries/InquiriesPage'));
const InquiryDetailPage = lazy(() => import('../features/inquiries/InquiryDetailPage'));
const ContactsPage = lazy(() => import('../features/contacts/ContactsPage'));
const ContactDetailPage = lazy(() => import('../features/contacts/ContactDetailPage'));
const OperationsPage = lazy(() => import('../features/operations/OperationsPage'));
const ShowcasePage = lazy(() => import('../features/showcase/ShowcasePage'));
const HomeShowcasePage = lazy(() => import('../features/showcase/HomeShowcasePage'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));

function withSuspense(element: JSX.Element): JSX.Element {
  return (
    <Suspense fallback={<div className="loading-block">Chargement…</div>}>
      {element}
    </Suspense>
  );
}

export const appRoutes: RouteObject[] = [
  {
    path: '/login',
    element: withSuspense(<LoginPage />),
  },
  {
    path: '/',
    element: (
      <AdminAuthGuard>
        <PortalAdminShell />
      </AdminAuthGuard>
    ),
    children: [
      { index: true, element: withSuspense(<DashboardPage />) },
      { path: 'clients', element: withSuspense(<AccountsPage />) },
      { path: 'clients/new', element: withSuspense(<CreateAccountPage />) },
      { path: 'clients/:accountId', element: withSuspense(<AccountDetailPage />) },
      { path: 'web-projects', element: withSuspense(<WebProjectsPage />) },
      { path: 'showcase', element: withSuspense(<ShowcasePage />) },
      { path: 'showcase-home', element: withSuspense(<HomeShowcasePage />) },
      { path: 'web-projects/new', element: withSuspense(<V4ProjectCreatePage />) },
      { path: 'web-projects/:projectId', element: withSuspense(<WebProjectDetailPage />) },
      { path: 'audit', element: withSuspense(<AuditPage />) },
      { path: 'inquiries', element: withSuspense(<InquiriesPage />) },
      { path: 'inquiries/:id', element: withSuspense(<InquiryDetailPage />) },
      { path: 'contacts', element: withSuspense(<ContactsPage />) },
      { path: 'contacts/:id', element: withSuspense(<ContactDetailPage />) },
      { path: 'operations', element: <Navigate to="/operations/jobs" replace /> },
      { path: 'operations/:section', element: withSuspense(<OperationsPage />) },
    ],
  },
  {
    path: '/404',
    element: withSuspense(<NotFoundPage />),
  },
  {
    path: '*',
    element: <Navigate to="/404" replace />,
  },
];

export default appRoutes;
